import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { allArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 md:pt-24 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 mb-4">
              <i className="ri-file-warning-line text-2xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-900 font-bold mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查看的文章不存在或已被移除</p>
            <Link
              to="/category?tab=articles"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": `https://qiezi.video/article/${article.slug}#article`,
        "headline": article.title,
        "description": article.summary,
        "datePublished": article.date,
        "author": {
          "@type": "Organization",
          "name": "茄子视频"
        },
        "publisher": {
          "@type": "Organization",
          "name": "茄子视频",
          "@id": "https://qiezi.video/#organization"
        },
        "inLanguage": "zh-CN",
        "isPartOf": { "@id": "https://qiezi.video/#website" }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "首页",
            "item": "https://qiezi.video/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "最新资讯",
            "item": "https://qiezi.video/category?tab=articles"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": article.title
          }
        ]
      },
      {
        "@type": "WebPage",
        "@id": `https://qiezi.video/article/${article.slug}#webpage`,
        "url": `https://qiezi.video/article/${article.slug}`,
        "name": article.title,
        "description": article.summary,
        "inLanguage": "zh-CN",
        "isPartOf": { "@id": "https://qiezi.video/#website" }
      }
    ]
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 md:pt-24">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/category?tab=articles" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">最新资讯</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
          </div>
        </div>

        {/* Article Header */}
        <section className="w-full px-4 md:px-6 pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-2.5 py-1 text-xs font-medium rounded-md bg-secondary-100 text-secondary-700 whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-sm text-foreground-400 whitespace-nowrap">{article.date}</span>
              <span className="text-sm text-foreground-400 whitespace-nowrap">{article.readTime} 阅读</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-900 font-bold mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              {article.summary}
            </p>
          </div>
        </section>

        {/* Featured Image */}
        <section className="w-full px-4 md:px-6 pb-10">
          <div className="max-w-4xl mx-auto">
            <div className="relative aspect-[16/9] rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="w-full px-4 md:px-6 pb-16">
          <div className="max-w-3xl mx-auto">
            <article
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </section>

        {/* Related Articles */}
        <section className="w-full px-4 md:px-6 pb-16 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-heading text-xl text-foreground-900 font-bold mb-6">相关文章</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {allArticles
                .filter((a) => a.id !== article.id)
                .slice(0, 2)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group cursor-pointer bg-white rounded-xl p-4 flex gap-4 hover:bg-background-50 transition-colors"
                  >
                    <div className="relative w-28 h-20 md:w-36 md:h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[11px] font-medium text-secondary-600 whitespace-nowrap">{related.category}</span>
                      <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mt-1">
                        {related.title}
                      </h4>
                      <span className="text-xs text-foreground-400 mt-1 block whitespace-nowrap">{related.date}</span>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
    </>
  );
}